import * as React from 'react';
import { Label, Box } from '@apia/theme';
import { IconButton } from '../../../../common';
import makeDocTypeTooltip from '../../../common/modals/documentUploadModal/makeDocTypeTooltip';
import useFieldLabelPropsSelector from './useFieldLabelPropsSelector';
import useTranslationProps from './useTranslationProps';
import { IFieldLabel } from './types';
import useStoreProps from './useStoreProps';
import RequiredBox from '../RequiredBox';
import { ApiaUtil } from '@apia/components';
import { uploaders } from '../../../../common/uploader/UploaderApi';
import { useMount } from '@apia/util';

export function parseFakeJSON(fakeJSON: string) {
  return [...fakeJSON.matchAll(/(\d+): '?([^']+)'?/g)].map(([, k, v]) => ({
    id: k,
    label: v,
  }));
}

const FieldLabel = ({
  avoidColon,
  htmlFor,
  required = false,
  children = null,
  tooltip = '',
  fontColor = '',
  isInputAsText = false,
  label = '',
  as = 'label',
}: IFieldLabel): JSX.Element | null => {
  const infoButtonRef = React.useRef<HTMLButtonElement | null>(null);

  const { noModify, fieldId } = useStoreProps();
  const { type, isReadonly, isDisabled } = useFieldLabelPropsSelector();
  const { onClickTranslate, shouldShowTranslateButton, initTranslations } =
    useTranslationProps({
      children,
      label,
    });
  useMount(() => {
    initTranslations();
  });
  if (label === '') return null;

  return (
    <>
      <Box
        {...window.getVariant('layout.execution.form.fieldLabelWrapper')}
        className="fieldLabelWrapper"
      >
        {as === 'span' && (
          <Box
            as="span"
            sx={{
              fontWeight: 'bold',
            }}
          >
            <RequiredBox
              label={label}
              avoidColon={avoidColon}
              required={required}
              labelNode={children}
              isFormReadonly={isInputAsText}
            />
          </Box>
        )}
        {as === 'label' && (
          <Label
            id={`label_${htmlFor}`}
            {...(tooltip ? { title: tooltip } : null)}
            {...(htmlFor ? { htmlFor } : null)}
            sx={{
              color: fontColor,
            }}
          >
            <RequiredBox
              label={label}
              avoidColon={avoidColon}
              required={required}
              labelNode={children}
              isFormReadonly={isInputAsText}
            />
          </Label>
        )}
        <Box className="fieldLabelWrapper__buttons">
          {type === 'file' && !isReadonly && !isDisabled && !noModify && (
            <IconButton
              onMouseEnter={() => {
                ApiaUtil.instance.tooltips.open({
                  attachToElement: infoButtonRef,
                  children: makeDocTypeTooltip(uploaders[fieldId]),
                });
              }}
              ref={infoButtonRef}
              {...window.getVariant('layout.common.components.infoButton')}
              icon="Info"
              aria-label={window.LBL_SUPPORTED_DOCTYPES}
              iconSize="iconSm"
            />
          )}
          {shouldShowTranslateButton && (
            <IconButton
              {...window.getVariant(
                'layout.common.modals.translation.translateModalButton',
              )}
              className="translateModalButton"
              type="button"
              aria-label={window.BTN_TRANSLATE}
              icon="Translate"
              onClick={onClickTranslate}
            />
          )}
        </Box>
      </Box>
    </>
  );
};

export default FieldLabel;
